<script lang="ts">
  import { onMount, tick } from 'svelte'
  import { highlightAll } from '@/utils/highlight'

  interface Props {
    hashid: string
  }

  let { hashid }: Props = $props()

  let name = $state('')
  let sourceType = $state('webhook')
  let inboundEmailAddress = $state<string | null>(null)
  let sourceConfig = $state<Record<string, unknown>>({})
  let webhookUrl = $state('')
  let signingSecret = $state('')
  let secretVisible = $state(false)
  let copiedWebhook = $state(false)
  let copiedSecret = $state(false)
  let copiedCurl = $state(false)
  let copiedEmail = $state(false)
  let loading = $state(true)
  let error = $state<string | null>(null)
  let curlCodeEl: HTMLElement | undefined = $state()
  let highlightDone = false

  function csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
  }

  onMount(async () => {
    try {
      const res = await fetch(`/triggers/${hashid}.json`, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
      })
      if (!res.ok) {
        error = `Failed to load trigger (${res.status})`
        return
      }
      const data = await res.json()
      name = data.trigger.name
      sourceType = data.trigger.source_type ?? 'webhook'
      inboundEmailAddress = data.trigger.inbound_email_address ?? null
      sourceConfig = data.trigger.source_config ?? {}
      webhookUrl = data.trigger.webhook_url
      signingSecret = data.trigger.signing_secret
    } catch {
      error = 'Network error — please reload.'
    } finally {
      loading = false
    }
  })

  function maskedSecret(s: string): string {
    if (!s) return ''
    if (s.length <= 8) return '•'.repeat(s.length)
    return s.slice(0, 4) + '•'.repeat(Math.max(s.length - 8, 4)) + s.slice(-4)
  }

  async function copyToClipboard(value: string, which: 'webhook' | 'secret' | 'curl' | 'email') {
    await navigator.clipboard.writeText(value)
    if (which === 'webhook') {
      copiedWebhook = true
      setTimeout(() => { copiedWebhook = false }, 1800)
    } else if (which === 'secret') {
      copiedSecret = true
      setTimeout(() => { copiedSecret = false }, 1800)
    } else if (which === 'email') {
      copiedEmail = true
      setTimeout(() => { copiedEmail = false }, 1800)
    } else {
      copiedCurl = true
      setTimeout(() => { copiedCurl = false }, 1800)
    }
  }

  let curlExample = $derived(buildCurl(webhookUrl, signingSecret))

  $effect(() => {
    if (!loading && curlCodeEl && !highlightDone && curlExample) {
      highlightDone = true
      tick().then(() => highlightAll(curlCodeEl?.parentElement ?? document))
    }
  })

  function buildCurl(url: string, secret: string): string {
    if (!url || !secret) return ''
    return [
      `# Replace the body with your real event payload, then run.`,
      `BODY='{"event":"test","note":"hello from txtsignal"}'`,
      `TS=$(date +%s)`,
      `SIG=$(printf "%s.%s" "$TS" "$BODY" | openssl dgst -sha256 -hmac "${secret}" -hex | awk '{print $NF}')`,
      ``,
      `curl -X POST "${url}" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -H "X-Txtsignal-Timestamp: $TS" \\`,
      `  -H "X-Txtsignal-Signature: sha256=$SIG" \\`,
      `  -d "$BODY"`,
    ].join('\n')
  }
</script>

<div class="ns">
  {#if loading}
    <div class="ns-loading" aria-busy="true">
      <div class="ns-spinner"></div>
      <p>Loading…</p>
    </div>
  {:else if error}
    <div class="ns-error" role="alert">{error}</div>
  {:else}
    <div class="ns-celebrate">
      <div class="ns-celebrate__icon">🎉</div>
      <h1 class="ns-celebrate__title">"{name}" is ready</h1>
      <p class="ns-celebrate__lede">
        {#if sourceType === 'email'}
          Forward any alert email to the address below — your reviewer will receive a draft for each one.
        {:else if sourceType === 'weather'}
          Your trigger is watching the National Weather Service. When a matching alert is issued, your reviewer will receive a draft ready to send.
        {:else}
          Three things left to connect your source. Copy the URL and secret into your
          integration, then send a test event to confirm everything works end-to-end.
        {/if}
      </p>
    </div>

    {#if sourceType === 'weather'}
      <div class="ns-weather">
        <h3 class="ns-weather__heading">Your trigger is watching for these alerts:</h3>
        <ul class="ns-weather__list">
          {#each (sourceConfig.event_types as string[] ?? []) as evt}
            <li>{evt}</li>
          {/each}
        </ul>
        <p class="ns-weather__detail">
          Forecast zones: <code class="ns-inline">{((sourceConfig.nws_zones as string[]) ?? []).join(', ')}</code>
        </p>
        <p class="ns-weather__detail">
          When a matching alert is issued by the National Weather Service, you'll get an SMS with a draft to review.
        </p>
        <p class="ns-weather__detail">
          <a href="/docs/triggers/connect-weather-alerts" class="ns-link">Read the full setup guide →</a>
        </p>
      </div>
    {:else if sourceType === 'email'}
      <ol class="ns-steps">
        <li>
          <div class="ns-step__head">
            <span class="ns-step__num">1</span>
            <h2 class="ns-step__title">Forward emails to this address</h2>
          </div>
          <p class="ns-step__hint">
            Any email sent to this address will produce a draft for your reviewer:
          </p>
          <div class="ns-copy-row">
            <code class="ns-monobox">{inboundEmailAddress}</code>
            <button type="button" class="ns-btn ns-btn--ghost" onclick={() => copyToClipboard(inboundEmailAddress ?? '', 'email')}>
              {copiedEmail ? 'Copied ✓' : 'Copy address'}
            </button>
          </div>
        </li>

        <li>
          <div class="ns-step__head">
            <span class="ns-step__num">2</span>
            <h2 class="ns-step__title">Send a test email</h2>
          </div>
          <p class="ns-step__hint">
            Try forwarding a recent alert email to test it. On a successful delivery, your
            reviewer will receive a draft text within a few seconds.
          </p>
        </li>
      </ol>

      <p class="ns-doc-link">
        <a href="/docs/triggers/forward-an-email" class="ns-link">Read the full setup guide →</a>
      </p>
    {:else if sourceType === 'webhook'}
      <ol class="ns-steps">
        <li>
          <div class="ns-step__head">
            <span class="ns-step__num">1</span>
            <h2 class="ns-step__title">Copy the webhook URL into your tool</h2>
          </div>
          <p class="ns-step__hint">
            This is where your system POSTs each event. It's unique to this trigger and never changes.
          </p>
          <div class="ns-copy-row">
            <code class="ns-monobox">{webhookUrl}</code>
            <button type="button" class="ns-btn ns-btn--ghost" onclick={() => copyToClipboard(webhookUrl, 'webhook')}>
              {copiedWebhook ? 'Copied ✓' : 'Copy URL'}
            </button>
          </div>
        </li>

        <li>
          <div class="ns-step__head">
            <span class="ns-step__num">2</span>
            <h2 class="ns-step__title">Copy the signing secret into your HMAC signer</h2>
          </div>
          <p class="ns-step__hint">
            Use it to compute an HMAC-SHA256 over <code class="ns-inline">&lt;timestamp&gt;.&lt;body&gt;</code> and send the
            hex digest as the <code class="ns-inline">X-Txtsignal-Signature</code> header. Most integration
            platforms (Zapier, Make.com, n8n) have a built-in HMAC helper. <strong>Treat this like a password</strong> —
            anyone with it can forge requests.
          </p>
          <div class="ns-copy-row">
            <code class="ns-monobox">{secretVisible ? signingSecret : maskedSecret(signingSecret)}</code>
            <button type="button" class="ns-btn ns-btn--ghost" onclick={() => { secretVisible = !secretVisible }}>
              {secretVisible ? 'Hide' : 'Show'}
            </button>
            <button type="button" class="ns-btn ns-btn--ghost" onclick={() => copyToClipboard(signingSecret, 'secret')}>
              {copiedSecret ? 'Copied ✓' : 'Copy secret'}
            </button>
          </div>
        </li>

        <li>
          <div class="ns-step__head">
            <span class="ns-step__num">3</span>
            <h2 class="ns-step__title">Send a test event</h2>
          </div>
          <p class="ns-step__hint">
            Paste this into a terminal to confirm your signing works. On a 202 response, your
            reviewer will receive a text within a few seconds.
          </p>
          <div class="ns-curl">
            <pre class="ns-curl__code"><code class="language-bash" bind:this={curlCodeEl}>{curlExample}</code></pre>
            <button type="button" class="ns-btn ns-btn--ghost ns-curl__copy" onclick={() => copyToClipboard(curlExample, 'curl')}>
              {copiedCurl ? 'Copied ✓' : 'Copy command'}
            </button>
          </div>
        </li>
      </ol>
    {/if}

    <div class="ns-footnote">
      You can come back to these values anytime from the trigger's edit page.
    </div>
  {/if}
</div>

<style lang="scss">
  .ns {
    max-width: 720px;
    padding: 1.5rem 0 4rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #1a1a1a;
  }

  .ns-loading, .ns-error {
    padding: 2rem 0;
    color: #888;
    font-size: 0.9rem;
    text-align: center;
  }

  .ns-spinner {
    width: 28px;
    height: 28px;
    margin: 0 auto 0.75rem;
    border: 3px solid #e0e0e0;
    border-top-color: #4a90e2;
    border-radius: 50%;
    animation: ns-spin 0.7s linear infinite;
  }

  @keyframes ns-spin { to { transform: rotate(360deg); } }

  .ns-celebrate {
    text-align: center;
    padding: 1.5rem 0 2rem;
    border-bottom: 1px solid #eee;
    margin-bottom: 2rem;
  }

  .ns-celebrate__icon {
    font-size: 2.4rem;
    margin-bottom: 0.5rem;
  }

  .ns-celebrate__title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
  }

  .ns-celebrate__lede {
    color: #555;
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 36rem;
    margin: 0 auto;
  }

  .ns-steps {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .ns-steps > li {
    padding: 1.4rem 0;
    border-bottom: 1px solid #eee;

    &:last-child { border-bottom: none; }
  }

  .ns-step__head {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    margin-bottom: 0.45rem;
  }

  .ns-step__num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background: #4a90e2;
    color: #fff;
    font-weight: 700;
    font-size: 0.9rem;
  }

  .ns-step__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
  }

  .ns-step__hint {
    color: #555;
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0 0 0.75rem 2.45rem;
  }

  .ns-copy-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: 2.45rem;
    flex-wrap: wrap;
  }

  .ns-monobox {
    flex: 1 1 280px;
    background: #f1f3f6;
    border: 1px solid #e0e4ea;
    border-radius: 6px;
    padding: 0.55rem 0.75rem;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.85rem;
    color: #2c3e50;
    overflow-x: auto;
    white-space: nowrap;
  }

  .ns-inline {
    background: #eef1f5;
    border: 1px solid #e0e4ea;
    border-radius: 3px;
    padding: 0.05rem 0.3rem;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.82em;
    color: #2c3e50;
  }

  .ns-curl {
    position: relative;
    margin-left: 2.45rem;
  }

  .ns-curl__code {
    background: #1f2933;
    color: #e6edf3;
    border-radius: 8px;
    padding: 0.9rem 1rem;
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.78rem;
    line-height: 1.5;
    overflow-x: auto;
    margin: 0;
  }

  .ns-curl__copy {
    position: absolute;
    top: 0.6rem;
    right: 0.6rem;
    background: rgba(255,255,255,0.08);
    color: #e6edf3;
    border: 1px solid rgba(255,255,255,0.15);

    &:hover { background: rgba(255,255,255,0.15); }
  }

  .ns-btn {
    display: inline-block;
    padding: 0.45rem 0.9rem;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    background: transparent;
    transition: background 0.1s;
    font-family: inherit;
  }

  .ns-btn--ghost {
    color: #4a90e2;
    border-color: #cfd8e3;
    background: #fff;

    &:hover { background: #f6f9ff; }
  }

  .ns-footnote {
    margin-top: 2rem;
    color: #666;
    font-size: 0.85rem;
    font-style: italic;
  }

  .ns-weather {
    padding: 1.5rem 0;
  }

  .ns-weather__heading {
    font-size: 1.05rem;
    font-weight: 600;
    margin: 0 0 0.75rem;
  }

  .ns-weather__list {
    margin: 0 0 1rem 1.25rem;
    padding: 0;
    font-size: 0.9rem;
    color: #333;

    li { margin-bottom: 0.3rem; }
  }

  .ns-weather__detail {
    font-size: 0.9rem;
    color: #555;
    line-height: 1.5;
    margin: 0.5rem 0;
  }

  .ns-link {
    color: #4a90e2;
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }

  .ns-doc-link {
    margin: 1.25rem 0 0;
    font-size: 0.9rem;
    color: #555;
  }
</style>
